/**
 * Mini App auth glue (T-831).
 *
 * Reads Telegram WebApp.initData on boot, exchanges it for our JWT via
 * `POST /api/v1/telegram/auth`, and keeps the access token in memory.
 * Refresh tokens live in Telegram's CloudStorage so a soft reload of
 * the Mini App can resume without re-auth. The in-memory access token
 * is the only thing API calls send out.
 */
import WebApp from '@twa-dev/sdk';

import { apiBase } from '@/lib/env';

const CLOUD_REFRESH_KEY = 'rm_refresh';

interface AuthState {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

let state: AuthState | null = null;

function setState(next: AuthState | null): void {
  state = next;
}

export function currentAccessToken(): string | null {
  return state?.accessToken ?? null;
}

export function isAuthenticated(): boolean {
  return state !== null && state.expiresAt > Date.now();
}

// CloudStorage needs Bot API 6.9+. On older Telegram clients the method
// exists on the SDK shim but throws ``WebAppMethodUnsupported`` synchronously
// when invoked — which previously bubbled out of ensureSession() and showed
// as "Не вдалося увійти / WebAppMethodUnsupported". CloudStorage is only an
// optional refresh-token cache, so every call degrades gracefully to "no
// stored value": the initData path still authenticates.
async function cloudGet(key: string): Promise<string | null> {
  return new Promise((resolve) => {
    // Some clients expose getItem but never invoke its callback — without a
    // timer the boot promise (and the whole app) hangs on «Завантаження…».
    const timer = window.setTimeout(() => resolve(null), 3000);
    try {
      if (!WebApp.CloudStorage || typeof WebApp.CloudStorage.getItem !== 'function') {
        window.clearTimeout(timer);
        resolve(null);
        return;
      }
      WebApp.CloudStorage.getItem(key, (err, value) => {
        window.clearTimeout(timer);
        resolve(err ? null : value || null);
      });
    } catch {
      window.clearTimeout(timer);
      resolve(null);
    }
  });
}

async function cloudSet(key: string, value: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      if (!WebApp.CloudStorage || typeof WebApp.CloudStorage.setItem !== 'function') {
        resolve();
        return;
      }
      WebApp.CloudStorage.setItem(key, value, () => resolve());
    } catch {
      resolve();
    }
  });
}

async function cloudRemove(key: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      if (!WebApp.CloudStorage || typeof WebApp.CloudStorage.removeItem !== 'function') {
        resolve();
        return;
      }
      WebApp.CloudStorage.removeItem(key, () => resolve());
    } catch {
      resolve();
    }
  });
}

// Old WebViews lack AbortSignal.timeout — fall back to an untimed fetch there.
function fetchTimeout(): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(15_000)
    : undefined;
}

async function postJSON<T>(path: string, body: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }
  let response: Response;
  try {
    response = await fetch(`${apiBase()}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: fetchTimeout(),
    });
  } catch (err) {
    const name = err instanceof Error ? err.name : '';
    if (name === 'TimeoutError' || name === 'AbortError') {
      throw new Error('Сервер не відповідає — перевірте зʼєднання і спробуйте ще раз.');
    }
    throw err;
  }
  if (!response.ok) {
    // statusText is an empty string over HTTP/2 — never rely on it alone.
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const json = (await response.json()) as {
        error?: { message?: string };
        detail?: string;
        message?: string;
      };
      detail = (json.error && json.error.message) || json.detail || json.message || detail;
    } catch {
      // ignore — keep the fallback
    }
    throw new Error(detail);
  }
  return (await response.json()) as T;
}

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

function applyAuthResponse(payload: AuthResponse): void {
  setState({
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + Math.max(0, payload.expires_in - 30) * 1000,
  });
  void cloudSet(CLOUD_REFRESH_KEY, payload.refresh_token);
}

/**
 * Resolve a usable JWT before any API call.
 *
 * Tries (in order): in-memory token if still fresh → refresh via stored
 * refresh token → initData round-trip with the backend.
 */
export async function ensureSession(): Promise<string> {
  if (isAuthenticated() && state) {
    return state.accessToken;
  }
  // Refresh path
  const stored = await cloudGet(CLOUD_REFRESH_KEY);
  if (stored) {
    try {
      const refreshed = await postJSON<AuthResponse>('/api/v1/auth/refresh', {
        refresh_token: stored,
      });
      applyAuthResponse(refreshed);
      return refreshed.access_token;
    } catch {
      await cloudRemove(CLOUD_REFRESH_KEY);
    }
  }
  // initData path
  const initData = WebApp.initData;
  if (!initData) {
    throw new Error('Telegram initData is empty — open the Mini App from Telegram.');
  }
  const fresh = await postJSON<AuthResponse>('/api/v1/telegram/auth', {
    init_data: initData,
  });
  applyAuthResponse(fresh);
  return fresh.access_token;
}

/** Drop the in-memory token so the next ensureSession() re-authenticates
 * instead of returning the same (already rejected) access token. */
export function clearSession(): void {
  setState(null);
}

export async function logout(): Promise<void> {
  setState(null);
  await cloudRemove(CLOUD_REFRESH_KEY);
}
